/**
 * Public order-status page.
 *
 * Lets a buyer who has their public token (we put it in the
 * /thank-you URL and link from there) re-trigger email delivery if the
 * Gmail inbox missed the first attempt. Rate-limited server-side
 * (3 resends per process lifetime).
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useLang } from '@/i18n/useLang';
import { StorefrontError, storefrontApi } from '@/api/storefront';
import { StorefrontHeader } from '@/components/storefront/StorefrontHeader';

export function OrderStatusPage() {
  const { token } = useParams<{ token: string }>();
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  const statusQ = useQuery({
    queryKey: ['storefront', 'order', token],
    queryFn: () => storefrontApi.orderStatus(token!),
    enabled: !!token,
  });

  const resendMut = useMutation({
    mutationFn: () => storefrontApi.resendOrder(token!),
    onSuccess: (res) => {
      setResendMsg(
        t(
          `दोबारा भेजा गया। शेष प्रयास: ${res.attempts_remaining}`,
          `Resent. Attempts remaining: ${res.attempts_remaining}`,
        ),
      );
    },
    onError: (err) => {
      if (err instanceof StorefrontError && err.detail === 'too_many_resends') {
        setResendMsg(
          t('बहुत अधिक प्रयास — कृपया कुछ देर बाद कोशिश करें।',
            'Too many attempts — please try again in a while.'),
        );
      } else {
        setResendMsg(String(err));
      }
    },
  });

  if (!token) return null;

  return (
    <>
      <StorefrontHeader />
      <main className="mx-auto max-w-screen-md px-4 py-10">
        <div className="rounded-2xl bg-stone-900/60 p-6 ring-1 ring-stone-800">
          <h1 className="text-xl font-bold text-saffron-100">
            {t('आपका आदेश', 'Your order')}
          </h1>

          {statusQ.isLoading && (
            <p className="mt-3 text-sm text-stone-400">
              {t('लोड हो रहा है…', 'Loading…')}
            </p>
          )}
          {statusQ.isError && (
            <p className="mt-3 text-sm text-rose-300">
              {t('आदेश नहीं मिला।', 'Order not found.')}
            </p>
          )}

          {statusQ.data && (
            <dl className="mt-4 space-y-3 text-sm">
              <Row label={t('रिपोर्ट', 'Report')}
                   value={lang === 'hi' ? statusQ.data.report_title_hi : statusQ.data.report_title_en} />
              <Row label={t('ईमेल', 'Email')}
                   value={statusQ.data.buyer_email_masked} />
              <Row label={t('कीमत', 'Amount')}
                   value={statusQ.data.final_price_inr_display} />
              <Row label={t('स्थिति', 'Status')}
                   value={statusQ.data.status} />
              {statusQ.data.delivered_at && (
                <Row label={t('भेजा गया', 'Delivered at')}
                     value={new Date(statusQ.data.delivered_at).toLocaleString()} />
              )}
            </dl>
          )}

          {statusQ.data && (statusQ.data.status === 'paid' || statusQ.data.status === 'delivered') && (
            <div className="mt-6 border-t border-stone-800 pt-4">
              <button
                type="button"
                onClick={() => { setResendMsg(null); void resendMut.mutateAsync(); }}
                disabled={resendMut.isPending}
                className="rounded-md border border-saffron-600 px-3 py-2 text-sm text-saffron-200 hover:bg-saffron-700/30 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {resendMut.isPending
                  ? t('भेज रहे हैं…', 'Resending…')
                  : t('रिपोर्ट दोबारा भेजें', 'Resend report to my email')}
              </button>
              {resendMsg && (
                <p className="mt-2 text-xs text-stone-400">{resendMsg}</p>
              )}
            </div>
          )}

          <div className="mt-6 border-t border-stone-800 pt-4 text-xs">
            <Link to="/" className="text-stone-400 hover:text-saffron-200">
              ← {t('होम पर वापस', 'Back to home')}
            </Link>
          </div>
        </div>
      </main>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-stone-800/60 pb-2 last:border-0">
      <dt className="text-stone-500">{label}</dt>
      <dd className="text-stone-100">{value}</dd>
    </div>
  );
}
